/**
 * A real `componentDidCatch` boundary: `Suspense` alone only catches the
 * loading state, not a render error thrown by a resolved lazy component (or
 * a rejected dynamic import). Logs `componentId` and the error's
 * `name`/`message` only — never `error.cause`/props, which could carry
 * operational data (ADR-UI-003 §3 security note).
 */
import { Component } from "react";
import type { ReactNode } from "react";
import { ErrorState } from "../../ui/error-state";
import { workspaceText } from "../i18n";
import type { WorkspaceLocale } from "../i18n";
import type { WorkspaceComponentId } from "../types";

export interface WorkspaceErrorBoundaryProps {
  readonly componentId: WorkspaceComponentId;
  readonly locale?: WorkspaceLocale | undefined;
  readonly children: ReactNode;
}

interface WorkspaceErrorBoundaryState {
  failed: boolean;
}

export class WorkspaceErrorBoundary extends Component<WorkspaceErrorBoundaryProps, WorkspaceErrorBoundaryState> {
  override state: WorkspaceErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): WorkspaceErrorBoundaryState {
    return { failed: true };
  }

  override componentDidCatch(error: Error): void {
    console.error("Workspace panel failed to render", {
      componentId: this.props.componentId,
      errorName: error.name,
      errorMessage: error.message,
    });
  }

  override render(): ReactNode {
    if (this.state.failed) {
      const locale = this.props.locale ?? "en";
      return (
        <ErrorState
          description={this.props.componentId}
          title={workspaceText(locale, "workspaceComponentError")}
        />
      );
    }
    return this.props.children;
  }
}
